import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import { ref } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import { hasPermission } from '@/composables/usePermission'
import useAuditLog from '@/composables/useAuditLog'

export default function useCrimeEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { updateItem, getItem } = useLambda()
  const { addAuditEntry } = useAuditLog()
  const userData = store.state?.userStore?.userData
  const crime = ref({})
  const isSubmitting = ref(false)
  const originalData = ref(null)
  const query = {
    _id: oid(route.value.params.id),
    client_id: oid(userData.client.$oid),
    deleted: { $ne: true },
  }
  const collection = 'crime'

  async function show() {
    isSubmitting.value = true

    try {
      const item = await getItem({ collection, query })

      if (!item) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        router.push({ name: 'organization-crime-list' })
        return
      }

      // Inicializar history si no existe (documentos legacy)
      if (!item.history) {
        item.history = []
      }

      crime.value = item
      originalData.value = JSON.parse(JSON.stringify(item))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.crime_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  async function update(data) {
    if (!hasPermission('crimes', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    isSubmitting.value = true

    try {
      const payload = {}
      if (data.name?.value) payload.name = data.name.value
      payload.description = data.description?.value || ''
      payload.lawNumber = data.lawNumber?.value || ''
      payload.law = data.law?.value || ''
      payload.article = data.article?.value || ''
      payload.number = data.number?.value || ''

      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Add audit log entry
      await addAuditEntry({
        collection,
        documentId: oid(route.value.params.id),
        originalData: originalData.value,
        updatedData: {
          name: payload.name,
          description: payload.description,
          lawNumber: payload.lawNumber,
          law: payload.law,
          article: payload.article,
          number: payload.number,
        },
      })

      showSuccessMessage(i18n.t('message.crime_updated'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.crime_update_error'))
    } finally {
      router.push({ name: 'organization-crime-list' })
      isSubmitting.value = false
    }
  }

  return {
    update,
    show,
    crime,
    isSubmitting,
  }
}
